import { createAsyncThunk } from "@reduxjs/toolkit";
import { axiosDisponibility } from "../../../api/axios";
import { AVAILABILITY_PATHS } from "../../../api/endpoints";

const dbg = (...a) => {
  if (window.__AVAILABILITY_DEBUG__ === false) return;
};

/* ===================== FETCH TOUS LES SLOTS D'UNE JOURNÉE ===================== */

export const fetchDoctorSlotsForDay = createAsyncThunk(
  "availability/fetchDoctorSlotsForDay",
  async ({ medecinId, date }, { rejectWithValue }) => {
    try {
      if (!medecinId || !date) {
        throw new Error("medecinId and date are required");
      }

      const url = AVAILABILITY_PATHS.GET_SLOTS_FOR_DAY(medecinId, date);
      dbg("GET slots for day:", url);

      const res = await axiosDisponibility.get(url);
      const slots = res.data || [];

      return { medecinId, date, slots };
    } catch (err) {
      dbg("ERROR fetchDoctorSlotsForDay:", err);
      return rejectWithValue(
        err?.response?.data || err.message || "Failed to fetch slots"
      );
    }
  }
);

/* ===================== FETCH SLOTS DISPONIBLES D'UNE JOURNÉE ===================== */

export const fetchAvailableSlotsForDay = createAsyncThunk(
  "availability/fetchAvailableSlotsForDay",
  async ({ medecinId, date }, { rejectWithValue }) => {
    try {
      if (!medecinId || !date) {
        throw new Error("medecinId and date are required");
      }

      const url = AVAILABILITY_PATHS.GET_AVAILABLE_SLOTS(medecinId, date);
      dbg("GET available slots:", url);

      const res = await axiosDisponibility.get(url);
      const slots = res.data || [];

      return { medecinId, date, slots };
    } catch (err) {
      dbg("ERROR fetchAvailableSlotsForDay:", err);
      return rejectWithValue(
        err?.response?.data || err.message || "Failed to fetch available slots"
      );
    }
  }
);

/* ===================== CREATE RANGE (doctor range → slots 30min côté MS) ===================== */

export const createAvailabilityRange = createAsyncThunk(
  "availability/createAvailabilityRange",
  /**
   * payload: { medecinId, startTime, endTime, isAvailable? }
   * startTime / endTime : string "YYYY-MM-DD HH:mm" ou ISO
   */
  async ({ medecinId, startTime, endTime, isAvailable = true }, { rejectWithValue }) => {
    try {
      if (!medecinId || !startTime || !endTime) {
        throw new Error("medecinId, startTime and endTime are required");
      }

      const url = AVAILABILITY_PATHS.CREATE_RANGE(medecinId);
      dbg("POST createAvailabilityRange:", url, { startTime, endTime, isAvailable });

      const res = await axiosDisponibility.post(url, {
        startTime,
        endTime,
        isAvailable,
      });

      // le MS peut renvoyer un tableau de slots ou un slot unique
      return res.data;
    } catch (err) {
      dbg("ERROR createAvailabilityRange:", err);
      return rejectWithValue(
        err?.response?.data || err.message || "Failed to create availability range"
      );
    }
  }
);

/* ===================== UPDATE SLOT ===================== */

export const updateAvailabilitySlot = createAsyncThunk(
  "availability/updateAvailabilitySlot",
  async ({ id, startTime, endTime, isAvailable }, { rejectWithValue }) => {
    try {
      if (!id) {
        throw new Error("id is required");
      }

      const payload = {};
      if (startTime) payload.startTime = startTime;
      if (endTime) payload.endTime = endTime;
      if (typeof isAvailable === "boolean") payload.isAvailable = isAvailable;

      const url = AVAILABILITY_PATHS.UPDATE(id);
      dbg("PATCH updateAvailabilitySlot:", url, payload);

      const res = await axiosDisponibility.patch(url, payload);
      return res.data;
    } catch (err) {
      dbg("ERROR updateAvailabilitySlot:", err);
      return rejectWithValue(
        err?.response?.data || err.message || "Failed to update availability slot"
      );
    }
  }
);

/* ===================== DELETE SLOT ===================== */

export const deleteAvailabilitySlot = createAsyncThunk(
  "availability/deleteAvailabilitySlot",
  async (id, { rejectWithValue }) => {
    try {
      if (!id) {
        throw new Error("id is required");
      }

      const url = AVAILABILITY_PATHS.DELETE(id);
      dbg("DELETE deleteAvailabilitySlot:", url);

      await axiosDisponibility.delete(url);
      return id;
    } catch (err) {
      dbg("ERROR deleteAvailabilitySlot:", err);
      return rejectWithValue(
        err?.response?.data || err.message || "Failed to delete availability slot"
      );
    }
  }
);

/* ===================== MARK RANGE (bloc complet) ===================== */

export const markRangeUnavailable = createAsyncThunk(
  "availability/markRangeUnavailable",
  async ({ medecinId, start, end }, { rejectWithValue }) => {
    try {
      const url = AVAILABILITY_PATHS.MARK_RANGE_UNAVAILABLE;
      dbg("POST markRangeUnavailable:", url, { startTime: start, endTime: end });

      // ✅ N'envoyez QUE startTime et endTime
      const res = await axiosDisponibility.post(url, { 
        startTime: start,  // ✅ start devient startTime
        endTime: end       // ✅ end devient endTime
      });
      return res.data;
    } catch (err) {
      dbg("ERROR markRangeUnavailable:", err);
      return rejectWithValue(
        err?.response?.data || err.message || "Failed to mark range unavailable"
      );
    }
  }
);




export const markRangeAvailable = createAsyncThunk(
  "availability/markRangeAvailable",
  async ({ medecinId, start, end }, { rejectWithValue }) => {
    try {
      const url = AVAILABILITY_PATHS.MARK_RANGE_AVAILABLE;
      dbg("POST markRangeAvailable:", url, { startTime: start, endTime: end });

      // ✅ N'envoyez QUE startTime et endTime
      const res = await axiosDisponibility.post(url, { 
        startTime: start,  // ✅ start devient startTime
        endTime: end       // ✅ end devient endTime
      });
      return res.data;
    } catch (err) {
      dbg("ERROR markRangeAvailable:", err);
      return rejectWithValue(
        err?.response?.data || err.message || "Failed to mark range available"
      );
    }
  }
);

export const markSlotsUnavailable = createAsyncThunk(
  "availability/markSlotsUnavailable",
  async ({ medecinId, slots }, { rejectWithValue }) => {
    try {
      const url = AVAILABILITY_PATHS.MARK_SLOTS_UNAVAILABLE;
      dbg("POST markSlotsUnavailable:", url, { slots });

      // ✅ N'envoyez QUE slots
      const res = await axiosDisponibility.post(url, { slots });
      return res.data;
    } catch (err) {
      dbg("ERROR markSlotsUnavailable:", err);
      return rejectWithValue(
        err?.response?.data || err.message || "Failed to mark slots unavailable"
      );
    }
  }
);

export const markSlotsAvailable = createAsyncThunk(
  "availability/markSlotsAvailable",
  async ({ medecinId, slots }, { rejectWithValue }) => {
    try {
      const url = AVAILABILITY_PATHS.MARK_SLOTS_AVAILABLE;
      dbg("POST markSlotsAvailable:", url, { slots });

      // ✅ N'envoyez QUE slots
      const res = await axiosDisponibility.post(url, { slots });
      return res.data;
    } catch (err) {
      dbg("ERROR markSlotsAvailable:", err);
      return rejectWithValue(
        err?.response?.data || err.message || "Failed to mark slots available"
      );
    }
  }
);